import React, { useState, useEffect, useRef } from 'react';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { TestType } from '@/models/SharedTypes';
import type { RetakeReport } from '@/models/UserTypes';
import type { LicensingAnswers } from '@/features/licensing/types';
import { licensingPayload } from '@/features/licensing/flow';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { logger } from '@/utils/logger';
import { getAttributionData } from '@/utils/emailAttribution';
import { trackReferralForUser } from '@/utils/referralTracking';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { trackRedditEvent } from '@/utils/redditPixel';
import { isInAppBrowser, shouldUseRedirect } from '@/utils/browserEnv';
import { setPendingExamStatus } from '@/utils/pendingExamStatus';
import { trialUserDiagnosticFields } from '@/utils/onboardingDiagnosticPayload';
import { readDiagnosticResult } from '@/utils/diagnosticMemory';
import { CallTimeoutError } from '@/utils/callWithTimeout';
import {
  claimPublicDiagnosticHandoff,
  completeTrialSignup,
  createTrialUserFallback,
  provisionTrialWithTimeout,
  TrialProvisioningError,
  type PersonalizationPayload,
  type TrialSignupData,
} from '@/utils/trialSignup';
import { clearPassPlanMint } from '@/utils/passPlanAuth';
import { authBreadcrumb, authError } from '@/utils/authObservability';
import {
  savePendingSocialSignup,
  loadPendingSocialSignup,
  clearPendingSocialSignup,
  PendingSocialSignup,
} from '@/utils/pendingSocialSignup';
import type { User as FirebaseUser } from 'firebase/auth';
import { ArrowRight, Eye, EyeOff, Loader2, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppleLogo, GoogleLogo } from './SocialProviderIcons';
import { AgeConfirm, AGE_REQUIRED_MESSAGE } from './AgeConfirm';

interface TrialSignupFlowProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  /**
   * ADDITIVE ONLY. Personalization gathered by the account-LAST /onboarding flow
   * (name + a scheduled exam date) BEFORE this component mounts. It is merged into
   * the createTrialUser payload at every call site (direct provision, fallback
   * retry, and the social path) so the trial grant carries the visitor's name +
   * exam date in one shot. It changes NOTHING about auth / provisioning / timeout
   * / redirect / fallback control flow — only what fields ride in the payload.
   * Undefined/empty values contribute nothing (the legacy callers pass no prop).
   */
  personalization?: {
    name?: string;
    examDate?: string;
    /**
     * Optional prior-attempt report from the pre-account onboarding step. Rides
     * into createTrialUser, which hardens it into isRetaker + userSegment on the
     * user doc at creation. Sent ONLY when the visitor answered "Yes".
     */
    retakeReport?: RetakeReport;
    isRetaker?: boolean;
    /**
     * The jurisdiction step's answers (#411), already enum-validated at the
     * backend contract boundary. Flat, optional fields: a signup that never saw
     * the step (flag off, social redirect) sends none of them and is byte-for-
     * byte the payload it is today.
     */
    licensing?: LicensingAnswers;
  };
}

type SignupStep = 'initial' | 'email-password';

const TrialSignupFlow: React.FC<TrialSignupFlowProps> = ({ onSuccess, onError, personalization }) => {
  const { currentUser, signInWithGoogle, signInWithApple, startSocialRedirect, completeSocialRedirect } = useEnhancedAuth();
  const [step, setStep] = useState<SignupStep>('initial');
  const [formData, setFormData] = useState<TrialSignupData>(() => ({
    email: '',
    termsAccepted: true,
    marketingConsent: true,
    examType: TestType.LCSW,
    signupSource: 'email',
    userSegment: 'unknown',
    // isRetaker was plumbed to every createTrialUser call site but had no source
    // until the pre-account retake step. It arrives as a PROP from the screen
    // immediately before this one, so it is scoped to this signup and cannot
    // leak into a later unrelated one the way a storage key would. The backend
    // also derives it from `retakeReport`; both are sent so neither path is
    // silently authoritative.
    isRetaker: personalization?.isRetaker || undefined,
  }));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [showExamSelector, setShowExamSelector] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  // Set the instant THIS component starts creating an account. Without it, the
  // signed-in-continuer branch below would flash over the form the moment our
  // own signup sets currentUser, mid-provisioning. A ref, not state: it must be
  // true before the auth-driven re-render, and it must never cause one itself.
  const signupInFlightRef = useRef(false);

  useEffect(() => {
    const attribution = getAttributionData();
    if (attribution) {
      setFormData(prev => ({
        ...prev,
        acquisitionChannel: attribution.acquisitionChannel,
        utmParams: attribution.utmParams,
        schoolId: attribution.schoolId,
      }));
    }

    const urlParams = new URLSearchParams(window.location.search);
    const examParam = urlParams.get('exam') || urlParams.get('test');
    const examTypeMap: Record<string, TestType> = {
      'lbsw': TestType.LBSW,
      'lmsw': TestType.LMSW,
      'lcsw': TestType.LCSW,
    };
    if (examParam) {
      const detectedExamType = examTypeMap[examParam.toLowerCase()];
      if (detectedExamType) {
        setFormData(prev => ({ ...prev, examType: detectedExamType }));
      }
    } else {
      // Homepage 6Q already recorded the exam — don't default back to LCSW.
      const remembered = readDiagnosticResult();
      const fromMemory = remembered?.examType
        ? examTypeMap[remembered.examType.toLowerCase()]
        : undefined;
      if (fromMemory) {
        setFormData(prev => ({ ...prev, examType: fromMemory }));
      }
    }

    // /try's "when are you testing?" answer rides in on ?examStatus=. The
    // upcoming navigate('/trial-setup') drops query params, so stash it in
    // sessionStorage; Onboarding reads-and-clears it to pre-fill the scheduler.
    setPendingExamStatus(urlParams.get('examStatus'));
  }, []);

  const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');

  // The /onboarding personalization, reduced to the fields createTrialUser accepts.
  // Spread into each createTrialUser payload AFTER trialUserDiagnosticFields(...) so
  // an onboarding-entered name wins over a social displayName. Empty → contributes
  // nothing, so the legacy callers (no prop) are byte-for-byte unchanged.
  const personalizationPayload: PersonalizationPayload = {
    ...(personalization?.name?.trim() ? { name: personalization.name.trim() } : {}),
    ...(personalization?.examDate ? { examDate: personalization.examDate } : {}),
    ...(personalization?.retakeReport ? { retakeReport: personalization.retakeReport } : {}),
    // Answered fields only, via the same builder the profile write uses — an
    // unanswered question never becomes a stored value.
    ...licensingPayload(personalization?.licensing ?? {}),
  };

  const calculatePasswordStrength = (pwd: string): 'weak' | 'medium' | 'strong' | null => {
    if (!pwd) return null;
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  // Finish a signup once we have an authenticated user — shared by the social
  // popup path (desktop), the social redirect-return path (in-app browsers), and
  // the signed-in continuer whose account was already minted from a Pass Plan
  // token. Creates the trial, logs analytics, tracks referral, then hands off via
  // onSuccess. Auth is the caller's job; everything after it is this function's.
  const completeSignupForUser = async (
    user: FirebaseUser,
    ctx: Omit<PendingSocialSignup, 'provider'>,
    provider: 'google' | 'apple' | 'email'
  ) => {
    await completeTrialSignup(user, ctx, provider, personalizationPayload);
    onSuccess();
  };

  // On mount, finish any signup that came back from a redirect (in-app-browser
  // path). The redirect navigated the page away and back, so we rehydrate the
  // context we stashed before leaving and resolve the pending credential.
  useEffect(() => {
    const pending = loadPendingSocialSignup();
    if (!pending) return;
    signupInFlightRef.current = true;
    let cancelled = false;
    const setLoading = pending.provider === 'google' ? setIsGoogleLoading : setIsAppleLoading;
    setLoading(true);
    (async () => {
      try {
        const result = await completeSocialRedirect();
        if (cancelled) return;
        authBreadcrumb('redirect-return', {
          provider: pending.provider,
          has_result: !!result?.user,
        });
        if (result?.user?.email) {
          await completeSignupForUser(result.user, pending, pending.provider);
          authBreadcrumb('redirect-completion-success', {
            provider: pending.provider,
            uid: result.user.uid,
          });
          clearPendingSocialSignup();
        } else {
          // Returned with no credential — redirect was cancelled or dropped.
          clearPendingSocialSignup();
          authBreadcrumb('redirect-no-result', { provider: pending.provider });
          logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_FAILED, {
            signup_method: pending.provider,
            reason: 'redirect-no-result',
            in_app_browser: isInAppBrowser(),
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error) {
        clearPendingSocialSignup();
        const code = (error as { code?: string })?.code || '';
        const timedOut = error instanceof CallTimeoutError;
        authError('redirect-completion-failed', error, { provider: pending.provider });
        logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_FAILED, {
          signup_method: pending.provider,
          reason: code || 'redirect-error',
          in_app_browser: isInAppBrowser(),
          timestamp: new Date().toISOString(),
        });
        onError(
          timedOut
            ? 'This is taking longer than expected. Please try again.'
            : 'Sign-in didn’t complete. Please try again, or use email below.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Run once on mount to resolve a pending redirect; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ageErrorShownRef = useRef(false);
  const blockedByAge = () => {
    if (ageConfirmed) return false;
    ageErrorShownRef.current = true;
    onError(AGE_REQUIRED_MESSAGE);
    return true;
  };
  const handleAgeChange = (checked: boolean) => {
    setAgeConfirmed(checked);
    if (checked && ageErrorShownRef.current) {
      ageErrorShownRef.current = false;
      onError('');
    }
  };

  const handleSocialSignup = async (provider: 'google' | 'apple') => {
    if (blockedByAge()) return;
    signupInFlightRef.current = true;
    const setLoading = provider === 'google' ? setIsGoogleLoading : setIsAppleLoading;
    const signInMethod = provider === 'google' ? signInWithGoogle : signInWithApple;
    const ctx: PendingSocialSignup = {
      provider,
      marketingConsent: formData.marketingConsent,
      examType: formData.examType,
      userSegment: formData.userSegment,
      acquisitionChannel: formData.acquisitionChannel,
      utmParams: formData.utmParams,
      schoolId: formData.schoolId,
      isRetaker: formData.isRetaker,
      retakeReport: personalizationPayload.retakeReport,
    };

    setLoading(true);

    try {
      // Mobile browsers — in-app webviews (Gmail/Facebook/Instagram) AND regular
      // mobile Safari/Chrome — handle signInWithPopup unreliably (silent block /
      // "Sign-in cancelled"), the ~0% trial-completion cause. Use redirect there
      // PROACTIVELY (avoids the failed-popup flash). Stash context first because
      // the redirect destroys in-memory state; the mount effect above finishes
      // the job on return.
      if (shouldUseRedirect()) {
        savePendingSocialSignup(ctx);
        await startSocialRedirect(provider);
        return; // page navigates away
      }

      // Desktop: popup is the better UX and works.
      await signInMethod({
        marketingEmailConsent: formData.marketingConsent,
        transactionalEmailConsent: true,
        termsAcceptedAt: new Date().toISOString()
      }, true);

      if (!auth.currentUser?.email) {
        throw new Error('No email found after social auth');
      }
      await completeSignupForUser(auth.currentUser, ctx, provider);
    } catch (error) {
      const code = (error as { code?: string })?.code || '';
      const errorMessage = error instanceof Error ? error.message : `Failed to sign up with ${provider}`;
      const inApp = isInAppBrowser();
      console.error(`${provider} signup error:`, error);
      authError('social-signup-failed', error, { provider, in_app_browser: inApp });

      logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_FAILED, {
        signup_method: provider,
        reason: code || errorMessage,
        in_app_browser: inApp,
        timestamp: new Date().toISOString(),
      });

      // Genuine user-initiated cancel — don't shout at them.
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        setLoading(false);
        return;
      }

      // A hung trial callable (App Check reCAPTCHA can stall) timed out — the
      // auth itself succeeded, so don't fall back to redirect. Surface a clear
      // message and ALWAYS clear the spinner.
      if (error instanceof CallTimeoutError) {
        onError('This is taking longer than expected. Please try again.');
        setLoading(false);
        return;
      }

      // Popup blocked / unsupported on a non-in-app browser (e.g. an extension
      // blocking popups). Fall back to redirect instead of dead-ending on email.
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/web-storage-unsupported' ||
        code === 'auth/operation-not-supported-in-this-environment'
      ) {
        try {
          savePendingSocialSignup(ctx);
          await startSocialRedirect(provider);
          return; // page navigates away
        } catch (redirectError) {
          console.error(`${provider} redirect fallback failed:`, redirectError);
          onError(
            `${provider === 'apple' ? 'Apple' : 'Google'} sign-in was blocked. ` +
            `Use “sign up with email” below, or open this page in Safari or Chrome.`
          );
          setLoading(false);
          return;
        }
      }

      onError(errorMessage);
      setLoading(false);
    }
    // NOTE: no finally{setLoading(false)} — the redirect path must KEEP loading
    // true while the page navigates away. Each non-redirect exit clears it.
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (blockedByAge()) return;

    if (step === 'initial') {
      if (!formData.email) {
        onError('Please enter your email address');
        return;
      }
      setStep('email-password');
      return;
    }

    if (!password) {
      onError('Password is required');
      return;
    }

    if (password.length < 8) {
      onError('Password must be at least 8 characters');
      return;
    }

    // Friction cut: 8+ chars is the only gate. The strength meter stays as
    // guidance, but we no longer REJECT "weak" passwords or require a separate
    // confirm field — both were abandonment points with ~0 completed signups.

    setIsLoading(true);
    signupInFlightRef.current = true;

    try {
      // A Pass Plan redeem (or an earlier visit) may already own this address, so
      // "already in use" is now an ordinary outcome rather than a dead end: try
      // the password against the existing account and provision the trial onto
      // it. Never create a second account for the same email. If the password
      // doesn't match, rethrow the ORIGINAL error so the catch below keeps
      // showing the "try signing in instead" copy.
      let userCredential;
      let createdNow = true;
      try {
        userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
      } catch (createError) {
        createdNow = false;
        if ((createError as { code?: string })?.code !== 'auth/email-already-in-use') throw createError;
        try {
          userCredential = await signInWithEmailAndPassword(auth, formData.email, password);
        } catch {
          throw createError;
        }
      }

      logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_STARTED, {
        signup_method: 'email',
        user_segment: formData.userSegment,
        acquisition_channel: formData.acquisitionChannel,
      });

      // Provision the trial BEFORE routing forward. We must NOT report success
      // until the trial genuinely exists (callable, or callable-retry inside the
      // fallback). The fallback THROWS if it can't provision, which drops us into
      // the catch below — no success analytics, no onSuccess, clear error shown.
      try {
        await provisionTrialWithTimeout({
          email: formData.email,
          termsAccepted: true,
          marketingConsent: formData.marketingConsent,
          examType: formData.examType,
          signupSource: 'email',
          userSegment: formData.userSegment,
          acquisitionChannel: formData.acquisitionChannel,
          utmParams: formData.utmParams,
          ...(formData.schoolId ? { schoolId: formData.schoolId } : {}),
          isRetaker: formData.isRetaker,
          ...trialUserDiagnosticFields(),
          ...personalizationPayload,
        }, 'email');
      } catch (functionError) {
        // A rejected address (mistyped or disposable domain) fails the same way on
        // every retry. Undo the Auth account we just made so the visitor can fix the
        // email and try again, and show the server's reason instead of a half-made account.
        if ((functionError as { code?: string })?.code === 'functions/invalid-argument') {
          try {
            if (createdNow) await userCredential.user.delete();
            else await auth.signOut();
          } catch (cleanupErr) {
            logger.warn('TrialSignupFlow', 'cleanup after rejected signup failed', { cleanupErr });
            await auth.signOut().catch(() => undefined);
          }
          throw new Error(
            (functionError as Error).message || 'Please use a valid email address from a real email provider.'
          );
        }
        console.error('createTrialUser function error, using fallback:', functionError);
        await createTrialUserFallback(userCredential.user.uid, formData, personalizationPayload);
      }

      await claimPublicDiagnosticHandoff(userCredential.user.uid);

      logAnalyticsEvent(ANALYTICS_EVENTS.TRIAL_SIGNUP_COMPLETED, {
        signup_method: 'email',
        user_segment: formData.userSegment,
        acquisition_channel: formData.acquisitionChannel,
        exam_type: formData.examType,
        timestamp: new Date().toISOString(),
      });

      // Only for an inbox we haven't proved yet. A Pass Plan redeem already
      // stamped emailVerified, so re-asking would stack a third mail on top of
      // the results + workbook Pam just sent, to verify something already verified.
      if (!userCredential.user.emailVerified) {
        sendCustomVerificationEmail({}).catch((verificationError) => {
          console.error('Custom verification email failed:', verificationError);
          logger.warn('TrialSignupFlow', 'Verification email not sent', { error: verificationError });
        });
      }

      trackReferralForUser(userCredential.user.uid).catch((refError) => {
        logger.warn('TrialSignupFlow', 'Referral tracking failed (non-critical)', { error: refError });
      });

      // Ad-platform SignUp conversion - see the matching comment in
      // completeSignupForUser for why this point is reached only on a real
      // trial grant, and why the pixel call is wrapped.
      try {
        trackRedditEvent('SignUp');
      } catch (trackErr) {
        console.error('SignUp conversion tracking failed (non-fatal):', trackErr);
      }

      onSuccess();
    } catch (error: unknown) {
      console.error('Trial signup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create trial account';
      logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_FAILED, {
        signup_method: 'email',
        reason: (error as { code?: string })?.code || errorMessage,
        in_app_browser: isInAppBrowser(),
        timestamp: new Date().toISOString(),
      });
      if (error instanceof CallTimeoutError) {
        // The trial callable hung (App Check reCAPTCHA stall) and timed out —
        // spinner is cleared by finally; surface a clear, friendly retry prompt.
        onError('This is taking longer than expected. Please try again.');
      } else if (error instanceof TrialProvisioningError) {
        // Trial couldn't be provisioned — don't route them in as a trial user.
        onError(error.message);
      } else if (errorMessage.includes('email-already-in-use')) {
        onError('This email is already registered. Try signing in instead.');
      } else if (errorMessage.includes('invalid-email')) {
        onError('Please enter a valid email address.');
      } else if (errorMessage.includes('weak-password')) {
        onError('Please choose a stronger password.');
      } else {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const examTypeLabels: Record<TestType, string> = {
    [TestType.LCSW]: 'LCSW (Clinical)',
    [TestType.LMSW]: 'LMSW (Masters)',
    [TestType.LBSW]: 'LBSW (Bachelors)',
  };

  const isAnyLoading = isLoading || isGoogleLoading || isAppleLoading;

  // Someone whose account was already minted from a Pass Plan token needs a
  // TRIAL, not an account. Creating one would be impossible (email-already-in-use)
  // and pointless (their inbox is proven), so skip auth entirely and run the same
  // provisioning tail. `signupInFlightRef` keeps our OWN signup — which sets
  // currentUser mid-flight — from swapping this panel in over the live form.
  const continuer =
    currentUser && !signupInFlightRef.current && !loadPendingSocialSignup() ? currentUser : null;

  const handleContinueSignedIn = async () => {
    if (!continuer || blockedByAge()) return;
    signupInFlightRef.current = true;
    setIsLoading(true);
    clearPassPlanMint();
    try {
      await completeSignupForUser(
        continuer,
        {
          marketingConsent: formData.marketingConsent,
          examType: formData.examType,
          userSegment: formData.userSegment,
          acquisitionChannel: formData.acquisitionChannel,
          utmParams: formData.utmParams,
          schoolId: formData.schoolId,
          isRetaker: formData.isRetaker,
        },
        'email'
      );
    } catch (error) {
      console.error('Signed-in trial provisioning error:', error);
      logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_FAILED, {
        signup_method: 'email',
        reason: (error as { code?: string })?.code || 'continuer-provisioning-failed',
        in_app_browser: isInAppBrowser(),
        timestamp: new Date().toISOString(),
      });
      if (error instanceof CallTimeoutError) {
        onError('This is taking longer than expected. Please try again.');
      } else if (error instanceof TrialProvisioningError) {
        onError(error.message);
      } else {
        onError(error instanceof Error ? error.message : 'Failed to start your free trial');
      }
      // Let them fall back to the ordinary form (the fallback may also have
      // signed them out, which clears this panel on its own).
      signupInFlightRef.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseDifferentAccount = async () => {
    clearPassPlanMint();
    try {
      await auth.signOut();
    } catch (err) {
      logger.warn('TrialSignupFlow', 'signOut for different-account failed (non-fatal)', { err });
    }
  };

  if (continuer) {
    return (
      <div className="space-y-4" data-testid="signed-in-continuer">
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
          <p className="text-xs font-medium text-neutral-500">Signed in as</p>
          <p className="font-semibold text-neutral-900 break-all">{continuer.email}</p>
          <p className="text-xs text-neutral-500 mt-1">
            Your email is already confirmed from your Pass Plan. No password to create.
          </p>
        </div>

        <AgeConfirm checked={ageConfirmed} onChange={handleAgeChange} />

        <button
          type="button"
          onClick={handleContinueSignedIn}
          disabled={isLoading}
          data-testid="continue-signed-in-button"
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all disabled:opacity-60 shadow-ledge-brand"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <span>Start my 7 free days</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleUseDifferentAccount}
          disabled={isLoading}
          className="w-full text-sm text-neutral-500 hover:text-neutral-700 font-medium disabled:opacity-60"
        >
          Use a different account
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AgeConfirm checked={ageConfirmed} onChange={handleAgeChange} />

      {/* One-tap signup, PRIMARY. Hidden once the visitor has chosen email, so the
          password step's submit stays above the fold on an iPhone SE. */}
      {step === 'initial' && (
      <>
      <div className="space-y-3" data-testid="social-auth-buttons">
        <button
          type="button"
          onClick={() => handleSocialSignup('apple')}
          disabled={isAnyLoading}
          data-testid="apple-v1-button"
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-neutral-900 text-white font-semibold rounded-xl hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all disabled:opacity-60 shadow-ledge-brand"
        >
          {isAppleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <AppleLogo />
          )}
          <span>Continue with Apple</span>
        </button>
        <button
          type="button"
          onClick={() => handleSocialSignup('google')}
          disabled={isAnyLoading}
          data-testid="google-v1-button"
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-white border-2 border-neutral-200 text-neutral-800 font-semibold rounded-xl hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all disabled:opacity-60"
        >
          {isGoogleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <GoogleLogo />
          )}
          <span>Continue with Google</span>
        </button>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-neutral-100 text-neutral-500 text-xs font-medium">or sign up with email</span>
        </div>
      </div>
      </>
      )}

      {/* Email Form — secondary */}
      <form onSubmit={handleEmailSubmit} className="space-y-4">
        {/* Email Input */}
        <div className="relative">
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            disabled={isAnyLoading || step === 'email-password'}
            placeholder="Enter your email"
            className={`w-full px-4 py-3.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:opacity-70 ${
              step === 'email-password' ? 'pr-20 text-ellipsis' : ''
            }`}
            autoComplete="email"
          />
          {step === 'email-password' && (
            <button
              type="button"
              onClick={() => setStep('initial')}
              className="absolute right-1 top-1/2 -translate-y-1/2 min-h-[44px] px-3 text-sm text-primary-600 hover:underline font-medium"
            >
              Change
            </button>
          )}
        </div>

        {/* Password Input - Shows after email */}
        {step === 'email-password' && (
          <div className="space-y-3 animate-fade-in">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Create a password (8+ characters)"
                className="w-full px-4 py-3.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all pr-12"
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-1 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center text-neutral-400 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      passwordStrength === 'weak'
                        ? 'w-1/3 bg-error'
                        : passwordStrength === 'medium'
                        ? 'w-2/3 bg-warning'
                        : 'w-full bg-success'
                    }`}
                  />
                </div>
                <span
                  className={`text-xs font-medium ${
                    passwordStrength === 'weak'
                      ? 'text-error'
                      : passwordStrength === 'medium'
                      ? 'text-warning'
                      : 'text-success'
                  }`}
                >
                  {passwordStrength === 'weak' ? 'Weak' : passwordStrength === 'medium' ? 'Good' : 'Strong'}
                </span>
              </div>
            )}

          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isAnyLoading || !formData.email}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-ledge-brand"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Creating your account...</span>
            </>
          ) : step === 'initial' ? (
            <>
              <span>Continue with Email</span>
              <ArrowRight className="w-4 h-4" />
            </>
          ) : (
            <>
              <span>Get My 7 Free Days</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      {/* Exam Type Selector (Collapsible) */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowExamSelector(!showExamSelector)}
          className="w-full flex min-h-[44px] items-center justify-between text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <span>Preparing for: <span className="font-medium text-neutral-700">{examTypeLabels[formData.examType || TestType.LCSW]}</span></span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showExamSelector ? 'rotate-180' : ''}`} />
        </button>

        {showExamSelector && (
          <div className="mt-2 p-3 bg-neutral-50 rounded-xl space-y-1 border border-neutral-200">
            {[TestType.LCSW, TestType.LMSW, TestType.LBSW].map((type) => (
              <label key={type} className="flex items-center gap-3 cursor-pointer p-2.5 rounded-lg hover:bg-neutral-200 transition-colors">
                <input
                  type="radio"
                  name="examType"
                  checked={formData.examType === type}
                  onChange={() => setFormData(prev => ({ ...prev, examType: type }))}
                  className="w-4 h-4 text-primary-500 border-neutral-300 focus:ring-primary-500 bg-neutral-100"
                />
                <span className="text-sm text-neutral-700">
                  {examTypeLabels[type]}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Marketing Consent Toggle */}
      <label className="flex min-h-[44px] items-center gap-3 cursor-pointer group">
        <div className="relative flex items-center">
          <input
            type="checkbox"
            checked={formData.marketingConsent}
            onChange={(e) => setFormData(prev => ({ ...prev, marketingConsent: e.target.checked }))}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-neutral-200 peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500" />
        </div>
        <span className="text-sm text-neutral-600 leading-tight">
          Send me study tips and exam prep resources
        </span>
      </label>

      {/* Legal Text */}
      <p className="text-xs text-neutral-400 text-center leading-relaxed">
        By continuing, you agree to our{' '}
        <Link to="/terms" className="inline-flex min-h-[44px] items-center text-primary-600 hover:underline">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="inline-flex min-h-[44px] items-center text-primary-600 hover:underline">
          Privacy Policy
        </Link>
      </p>
    </div>
  );
};

export default TrialSignupFlow;
